import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ApConfig } from 'src/app/classes/models';
import {
  apConfigSupportsClientManaged,
  getApConfigDisplayLabel,
  getClientManagedIneligibleReason
} from 'src/app/client/admin/accounting/helpers/ap-vendor-grid-actions.helper';
import { ApConfigService } from 'src/app/services/ap-config.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-ap-vendor-mapper-ap-config-selection-modal',
  templateUrl: './ap-vendor-mapper-ap-config-selection-modal.component.html',
  styleUrl: './ap-vendor-mapper-ap-config-selection-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ApVendorMapperApConfigSelectionModalComponent implements OnInit {
  constructor(
    private activeModal: NgbActiveModal,
    private ApConfigService: ApConfigService,
    private ToastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  @Input() CompanyId?: number;
  /**
   * Hide the keyword / accounting-ID filter fields. The bulk Client Managed
   * upload (VPDW-4365) picks a configuration only — its AP IDs come from a CSV.
   */
  @Input() showFilters: boolean = true;
  /**
   * Disable configurations that cannot carry a Client Managed flag, and show
   * why. Off by default so the AP-vendor mapper is unaffected.
   */
  @Input() requireClientManagedSupport: boolean = false;

  apConfigs: ApConfig[] | null = null;
  selectedApConfig: ApConfig | null = null;

  legalNames: string[] = [];
  apVendorKeys: string[] = [];

  @ViewChild('tagInput') private tagInput!: ElementRef<HTMLElement>;
  @ViewChild('apVendorKeyTagInput')
  private apVendorKeyTagInput!: ElementRef<HTMLElement>;

  close(): void {
    this.activeModal.dismiss();
  }

  addTags(event: Event): void {
    // Stop Enter from inserting a newline/`<div>` into the contenteditable.
    event.preventDefault();

    const inputEl = this.tagInput.nativeElement;
    const words = (inputEl.textContent ?? '')
      .split(',')
      .map((word) => word.trim())
      .filter((word) => word.length > 0);

    for (const word of words) {
      if (!this.legalNames.includes(word)) {
        this.legalNames.push(word);
      }
    }

    inputEl.textContent = '';
  }

  removeTag(index: number, event: Event): void {
    // Don't let the chip click bubble up and refocus the input container.
    event.stopPropagation();
    this.legalNames.splice(index, 1);
  }

  focusTagInput(): void {
    this.tagInput.nativeElement.focus();
  }

  addapVendorKeys(event: Event): void {
    // Stop Enter from inserting a newline/`<div>` into the contenteditable.
    event.preventDefault();

    const inputEl = this.apVendorKeyTagInput.nativeElement;
    const apVendorKeys = (inputEl.textContent ?? '')
      .split(',')
      .map((apVendorKey) => apVendorKey.trim())
      .filter((apVendorKey) => apVendorKey.length > 0);

    for (const apVendorKey of apVendorKeys) {
      if (!this.apVendorKeys.includes(apVendorKey)) {
        this.apVendorKeys.push(apVendorKey);
      }
    }

    inputEl.textContent = '';
  }

  removeapVendorKeyTag(index: number, event: Event): void {
    // Don't let the chip click bubble up and refocus the input container.
    event.stopPropagation();
    this.apVendorKeys.splice(index, 1);
  }

  focusapVendorKeyTagInput(): void {
    this.apVendorKeyTagInput.nativeElement.focus();
  }

  isApConfigSelectable(apConfig: ApConfig): boolean {
    return (
      !this.requireClientManagedSupport ||
      apConfigSupportsClientManaged(apConfig)
    );
  }

  getIneligibleReason(apConfig: ApConfig): string | null {
    if (!this.requireClientManagedSupport) {
      return null;
    }

    return getClientManagedIneligibleReason(apConfig);
  }

  hasSelectableApConfig(): boolean {
    return (this.apConfigs ?? []).some((apConfig) =>
      this.isApConfigSelectable(apConfig)
    );
  }

  /**
   * Option text, with the ineligibility reason appended so a disabled row
   * explains itself in place rather than looking like a bug.
   */
  getApConfigOptionLabel(apConfig: ApConfig): string {
    const reason = this.getIneligibleReason(apConfig);
    const label = this.getApConfigLabel(apConfig);

    return reason ? `${label} — ${reason}` : label;
  }

  getApConfigLabel(ApConfig: ApConfig): string {
    return getApConfigDisplayLabel(ApConfig);
  }

  submit(): void {
    if (!this.selectedApConfig) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot select AP config. No AP config selected.',
        { returnError: true }
      );
    }

    this.activeModal.close({
      selectedApConfig: this.selectedApConfig,
      legalNames: this.legalNames,
      apVendorKeys: this.apVendorKeys
    });
  }

  ngOnInit(): void {
    if (!this.CompanyId) {
      this.activeModal.dismiss();
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot load AP configs. No Company ID found.',
        { returnError: true }
      );
    }

    this.ApConfigService.getApConfigs({ CompanyId: this.CompanyId }).subscribe({
      next: (apConfigs: ApConfig[]) => {
        this.apConfigs = apConfigs;

        const selectable = apConfigs.filter((apConfig) =>
          this.isApConfigSelectable(apConfig)
        );

        if (selectable.length === 1) {
          this.selectedApConfig = selectable[0];
        }

        this.cdr.markForCheck();
      }
    });
  }
}
